import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState } from 'react';
import { useAuthStore } from './stores/auth.store';
import { clearAuthAndCache } from './services/auth/auth.helper';
import { useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_STATUS } from './@shared/interfaces/user.interfaces';
import { useSyncUserTimezone } from './hooks/useSyncUserTimezone';

// This will be our authenticated layout wrapper
const AuthenticatedLayout = () => {
  const {
    isAuthenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
    logout,
    user: auth0User,
  } = useAuth0();

  const {
    setGetToken,
    profile,
    isLoadingProfile,
    profileError,
    fetchProfile,
    setUser,
  } = useAuthStore();

  useSyncUserTimezone(isAuthenticated);

  const [initialProfileAttempted, setInitialProfileAttempted] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Set the token getter function
  useEffect(() => {
    if (getAccessTokenSilently) {
      setGetToken(getAccessTokenSilently);
    }
  }, [getAccessTokenSilently, setGetToken]);

  // Set basic user info from Auth0 when authenticated
  useEffect(() => {
    if (isAuthenticated && auth0User) {
      // Store basic Auth0 user info in the store - this is just to have some data
      // until the full profile is loaded from the API
      setUser({
        email: auth0User.email || '',
        firstName:
          auth0User?.given_name || (auth0User?.name?.split(' ')[0] ?? ''),
        lastName:
          auth0User?.family_name ||
          (auth0User?.name && auth0User.name.split(' ').length > 1
            ? auth0User.name.split(' ').slice(1).join(' ')
            : ''),
        photoUrl: auth0User.picture,
        auth0Uid: auth0User.sub ? [auth0User.sub] : [],
        // Required fields with reasonable defaults
        totalTaskPoints: 0,
        dailyMessageData: {
          count: 0,
          monthCount: 0,
          lastMessageDate: new Date(),
        },
        settings: {
          isOnboarded: false,
          dailyMessageLimit: 0,
          monthlyMessageLimit: 0,
          accountStatus: ACCOUNT_STATUS.FREE,
          sendReminders: false,
          calendars: [],
        },
      });
    }
  }, [isAuthenticated, auth0User, setUser]);

  // Fetch complete profile data when authenticated
  useEffect(() => {
    if (isAuthenticated && !profile && !isLoadingProfile) {
      fetchProfile()
        .catch((err: any) => console.error('Error fetching profile:', err))
        .finally(() => setInitialProfileAttempted(true));
    }
  }, [isAuthenticated, profile, isLoadingProfile, fetchProfile]);

  // Check authentication status
  useEffect(() => {
    // If auth is done loading and user is not authenticated, redirect to login
    if (!isAuth0Loading && !isAuthenticated) {
      navigate('/login');
      return;
    }

    // Only check profile errors if we've made an initial attempt to load the profile
    // This prevents redirecting to login during initial application load
    if (initialProfileAttempted && !isLoadingProfile && profileError) {
      console.error('Profile error:', profileError);
      // Only log out if there was a serious error like authentication failure
      // Don't log out for network errors or temporary issues
      if (
        profileError.message.includes('Authentication required') ||
        profileError.message.includes('Invalid token')
      ) {
        clearAuthAndCache({
          redirect: true,
          logout,
          queryClient,
          redirectPage: '/login',
        });
      }
    }
  }, [
    isAuth0Loading,
    isAuthenticated,
    isLoadingProfile,
    profileError,
    initialProfileAttempted,
    navigate,
    logout,
    queryClient,
  ]);

  // Show loading indicator while Auth0 authenticates
  if (isAuth0Loading) {
    return <div>Loading...</div>;
  }

  // If not authenticated, will be redirected in useEffect above
  if (!isAuthenticated) {
    return null;
  }

  // We've verified authentication, so render the app
  // Don't require profile to be loaded - it will be fetched in the background
  return <Outlet />;
};

export default AuthenticatedLayout;
